import * as React from "react";
import { useState, useRef } from "react";
import { useFormContext, type FieldPath, type FieldPathValue } from "react-hook-form";
import { InvitationStatus, Packet, RoleType, User, PacketStatus } from '@promo/shared';
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { SelectOption } from '@/components/ui/editable-select';
import { ComboboxOption } from '@/components/ui/editable-combobox';
import { Spinner } from "@/components/ui/spinner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { type MarkdownMode } from '@/components/ui/editable-textarea';
import { PacketFields } from './PacketFields';
import { cn } from "@/lib/utils";
import { useInvitations, useSendInvitations } from "../hooks/useInvitations";
import { toast } from "@/hooks/use-toast";
import { validateCoManagerEmails } from "./CoManagersField";

interface EditablePacketSectionProps {
  packet: Packet;
  onUpdate: (updatedFields: PacketUpdatePayload) => void | Promise<unknown>;
  isUpdating: boolean;
  readOnly?: boolean;
  onDelete?: () => void;
  className?: string;
  currentUserRole: RoleType | '';
  onFocusChange?: (isFocused: boolean) => void;
  canViewCompletedDetails?: boolean;
  canInviteCoManagers?: boolean;
}

type PacketFormFields = Pick<Packet, 'currentTitle' | 'targetTitle' | 'lastPromotedDate' | 'year' | 'seniorLeaderId' | 'summary'>;
export type PacketUpdatePayload = Partial<PacketFormFields> & {
  removeCoManagerIds?: string[];
  removeCoManagerInvitationIds?: string[];
};

const getDateValue = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  return undefined;
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export function EditablePacketSection({ packet, onUpdate, isUpdating, readOnly = false, onDelete, className, currentUserRole, onFocusChange, canViewCompletedDetails, canInviteCoManagers = false }: EditablePacketSectionProps) {
  const form = useFormContext<PacketFormFields>();
  const queryClient = useQueryClient();

  const formRef = useRef<HTMLFormElement>(null);
  const [pendingChanges, setPendingChanges] = useState<Partial<PacketFormFields>>({});
  const [isFocused, setIsFocused] = useState(false);
  const [summaryMarkdownMode, setSummaryMarkdownMode] = useState<MarkdownMode>('edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [coManagerEmailInput, setCoManagerEmailInput] = useState('');
  const [coManagerValidationError, setCoManagerValidationError] = useState('');
  const [removedCoManagerIds, setRemovedCoManagerIds] = useState<string[]>([]);
  const [removedCoManagerInvitationIds, setRemovedCoManagerInvitationIds] = useState<string[]>([]);

  const setFocusState = (focus: boolean) => {
    const wasPreviouslyFocused = isFocused;
    setIsFocused(focus);
    // Reset markdown mode to edit when entering edit mode (only on initial focus)
    if (focus && !wasPreviouslyFocused) {
      setSummaryMarkdownMode('edit');
    }
    if (onFocusChange) {
      onFocusChange(focus);
    }
  };

  const seniorLeadersQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.SENIOR_LEADER],
    queryFn: () => get('/users', { role: RoleType.SENIOR_LEADER }),
  });
  const seniorLeaderOptions: SelectOption[] = seniorLeadersQuery.data?.map((u: User) => ({ value: u.id, label: u.name || u.email })) || [];

  const managerQuery = useQuery<User | null, Error>({
    queryKey: ['users', 'by-id', packet.managerId],
    queryFn: () => packet.managerId ? get(`/users/${packet.managerId}`) : Promise.resolve(null),
    enabled: !!packet.managerId,
  });
  const managerToDisplay = packet.managerId ? (managerQuery.data || { name: 'Loading...', email: '' }) : null;
  const managerName = managerToDisplay ? (managerToDisplay.name || managerToDisplay.email) : 'Not assigned';

  const coManagerIds = React.useMemo(
    () => Array.from(new Set(packet.managerIds ?? []))
      .filter(managerId => managerId && managerId !== packet.managerId),
    [packet.managerId, packet.managerIds]
  );

  const coManagersQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-ids', 'co-managers', packet.id, coManagerIds],
    queryFn: () => Promise.all(coManagerIds.map(managerId => get<User>(`/users/${managerId}`))),
    enabled: coManagerIds.length > 0,
  });

  const candidateQuery = useQuery<User | null, Error>({
    queryKey: ['users', 'by-id', packet.candidateId],
    queryFn: () => packet.candidateId ? get<User>(`/users/${packet.candidateId}`) : Promise.resolve(null),
    enabled: !!packet.candidateId,
  });

  const seniorLeaderQuery = useQuery<User | null, Error>({
    queryKey: ['users', 'by-id', packet.seniorLeaderId],
    queryFn: () => packet.seniorLeaderId ? get<User>(`/users/${packet.seniorLeaderId}`) : Promise.resolve(null),
    enabled: !!packet.seniorLeaderId,
  });

  const { data: managerInvitations = [] } = useInvitations(packet.id, RoleType.MANAGER, {
    enabled: !!packet.id,
  });
  const sendCoManagerInvitations = useSendInvitations();

  React.useEffect(() => {
    const keepExistingArrayWhenUnchanged = (previous: string[], next: string[]) =>
      previous.length === next.length && previous.every((value, index) => value === next[index])
        ? previous
        : next;

    setRemovedCoManagerIds(prev => {
      const next = prev.filter(userId => coManagerIds.includes(userId));
      return keepExistingArrayWhenUnchanged(prev, next);
    });
    setRemovedCoManagerInvitationIds(prev => {
      const next = prev.filter(invitationId =>
        managerInvitations.some(invitation =>
          invitation.id === invitationId && invitation.status === InvitationStatus.PENDING
        )
      );
      return keepExistingArrayWhenUnchanged(prev, next);
    });
  }, [coManagerIds, managerInvitations]);

  const removedCoManagerIdSet = React.useMemo(
    () => new Set(removedCoManagerIds),
    [removedCoManagerIds]
  );
  const removedCoManagerInvitationIdSet = React.useMemo(
    () => new Set(removedCoManagerInvitationIds),
    [removedCoManagerInvitationIds]
  );
  const coManagers = (coManagersQuery.data ?? []).filter(
    user => !removedCoManagerIdSet.has(user.id)
  );
  const pendingCoManagerInvitations = managerInvitations.filter(
    invitation =>
      invitation.status === InvitationStatus.PENDING &&
      !removedCoManagerInvitationIdSet.has(invitation.id)
  );
  const coManagerDuplicateEmails = [
    managerToDisplay?.email,
    candidateQuery.data?.email,
    seniorLeaderQuery.data?.email,
    ...coManagers.map(user => user.email),
    ...pendingCoManagerInvitations.map(invitation => invitation.inviteeEmail),
  ].filter((email): email is string => Boolean(email));
  const coManagerIdentityCount = new Set([
    ...coManagers.map(user => normalizeEmail(user.email)),
    ...pendingCoManagerInvitations.map(invitation => normalizeEmail(invitation.inviteeEmail)),
  ]).size;
  const hasPacketFieldChanges = Object.keys(pendingChanges).length > 0;
  const hasCoManagerInviteInput = coManagerEmailInput.trim().length > 0;
  const hasCoManagerRemovals =
    removedCoManagerIds.length > 0 || removedCoManagerInvitationIds.length > 0;
  const isSaving = isUpdating || sendCoManagerInvitations.isPending;

  const handleInviteCoManagers = async (emails: string[]) => {
    type InvitationResult = {
      success: { email: string; token?: string; acceptLink?: string }[];
      failed: { email: string; reason: string }[];
    };

    try {
      const result = await sendCoManagerInvitations.mutateAsync({
        packetId: packet.id,
        inviteeEmails: emails,
        roleToInvite: RoleType.MANAGER,
      }) as InvitationResult;

      await queryClient.invalidateQueries({ queryKey: ['packet', packet.id] });

      if (result.success.length > 0) {
        toast({
          title: 'Co-manager invitations sent',
          description: `Invited ${result.success.length} co-manager${result.success.length > 1 ? 's' : ''}.`,
        });
      }

      if (result.failed.length > 0) {
        return {
          successCount: result.success.length,
          error: result.failed.map(failure => `${failure.email}: ${failure.reason}`).join('; '),
        };
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred';
      toast({
        title: 'Failed to add co-managers',
        description: message,
        variant: 'destructive',
      });
      return { error: message };
    }
  };

  const titleList: ComboboxOption[] = [
    "Software Engineer 1", "Software Engineer 2", "Software Engineer 3", "Sr. Software Engineer",
    "Staff Software Engineer","Principal Engineer","Distinguished Engineer","Manager Engineering",
    "Sr. Manager Engineering","Director Engineering","Sr. Director Engineering","Vice President Engineering",
    "Fellow Engineer, VP","SDET","Sr. SDET","Staff SDET","Principal SDET",
  ].map(label => ({ value: label, label }));

  const currentYear = new Date().getFullYear();
  const assessmentYearOptions = [currentYear, currentYear + 1, currentYear + 2].map(y => ({ value: String(y), label: String(y) }));
  const summaryIsSpecificallyEditable =
    (
      packet.status === PacketStatus.MANAGER_SUMMARY ||
      packet.status === PacketStatus.SENIOR_LEADER_REVIEW ||
      packet.status === PacketStatus.HR_REVIEW
    ) &&
    (
      currentUserRole === RoleType.MANAGER ||
      currentUserRole === RoleType.SENIOR_LEADER
    );
  const canEditPacketDetails = !readOnly || summaryIsSpecificallyEditable;
  const canEnterEditMode = canEditPacketDetails || canInviteCoManagers;
  const editOverlayLabel = canEditPacketDetails ? 'Edit packet details' : 'Manage co-managers';

  const handleFieldChange = <Name extends FieldPath<PacketFormFields>>(
    field: Name,
    value: FieldPathValue<PacketFormFields, Name>
  ) => {
    form.setValue(field, value, { shouldValidate: true });
    
    // Only track as a pending change if the value actually differs from the original packet value
    let originalValue: string | Date | null | undefined;
    switch (field) {
      case 'seniorLeaderId':
        originalValue = packet.seniorLeaderId || '';
        break;
      case 'currentTitle':
        originalValue = packet.currentTitle || '';
        break;
      case 'targetTitle':
        originalValue = packet.targetTitle || '';
        break;
      case 'lastPromotedDate':
        originalValue = packet.lastPromotedDate;
        break;
      case 'year':
        originalValue = packet.year;
        break;
      case 'summary':
        originalValue = packet.summary || '';
        break;
      default:
        originalValue = null;
    }
    
    // Compare values, handling different types appropriately
    let hasChanged = false;
    if (field === 'lastPromotedDate') {
      // For dates, convert both to strings for comparison
      hasChanged = String(value) !== String(originalValue);
    } else {
      // For other fields, direct comparison
      hasChanged = value !== originalValue;
    }
    
    if (hasChanged) {
      setPendingChanges(prev => ({ ...prev, [field]: value }));
    } else {
      // Remove from pending changes if value matches original
      setPendingChanges(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCoManagerValidationError('');

    let coManagerEmailsToInvite: string[] = [];
    if (hasCoManagerInviteInput) {
      const validation = validateCoManagerEmails(coManagerEmailInput, {
        duplicateEmails: coManagerDuplicateEmails,
        existingInviteCount: coManagerIdentityCount,
      });
      if (validation.error) {
        setCoManagerValidationError(validation.error);
        return;
      }
      coManagerEmailsToInvite = validation.emails;
    }

    if (hasPacketFieldChanges || hasCoManagerRemovals) {
      await onUpdate({
        ...pendingChanges,
        ...(removedCoManagerIds.length > 0 ? { removeCoManagerIds: removedCoManagerIds } : {}),
        ...(removedCoManagerInvitationIds.length > 0
          ? { removeCoManagerInvitationIds: removedCoManagerInvitationIds }
          : {}),
      });
      setPendingChanges({});
      await queryClient.invalidateQueries({ queryKey: ['invitations'] });
    }

    if (coManagerEmailsToInvite.length > 0) {
      const result = await handleInviteCoManagers(coManagerEmailsToInvite);
      if (result?.error) {
        setCoManagerValidationError(result.error);
        return;
      }
      setCoManagerEmailInput('');
    }

    setFocusState(false);
  };

  const handleCancel = () => {
    setPendingChanges({});
    setCoManagerEmailInput('');
    setCoManagerValidationError('');
    setRemovedCoManagerIds([]);
    setRemovedCoManagerInvitationIds([]);
    form.reset({
      seniorLeaderId: packet.seniorLeaderId || '',
      currentTitle: packet.currentTitle || '',
      targetTitle: packet.targetTitle || '',
      lastPromotedDate: packet.lastPromotedDate || null,
      year: packet.year,
      summary: packet.summary || '',
    });
    setFocusState(false);
  };

  const handleCancelClick = () => {
    // Use pendingChanges to determine if there are actual changes
    // This is more reliable than comparing form values directly
    const hasChanges = hasPacketFieldChanges || hasCoManagerInviteInput || hasCoManagerRemovals;
    if (hasChanges) setShowCancelConfirm(true);
    else handleCancel();
  };

  const handleDiscardChanges = () => { handleCancel(); setShowCancelConfirm(false); };

  const handleFocus = () => {
    if (isSaving) return;

    if (canEnterEditMode) {
      setFocusState(true);
    }
  };

  return (
    <section onPointerDownCapture={handleFocus} className={cn('relative', className)}>
      {!isFocused && canEnterEditMode && !isSaving && (
        <button
          type="button"
          aria-label={editOverlayLabel}
          className="absolute inset-0 z-10 cursor-text appearance-none border-0 bg-transparent p-0"
          onClick={handleFocus}
        />
      )}
      <form ref={formRef} onSubmit={handleSubmit} className="contents">
        <PacketFields
          values={{
            seniorLeaderId: form.watch('seniorLeaderId') || packet.seniorLeaderId || '',
            currentTitle: form.watch('currentTitle') || packet.currentTitle || '',
            targetTitle: form.watch('targetTitle') || packet.targetTitle || '',
            lastPromotedDate: getDateValue(form.watch('lastPromotedDate')) || getDateValue(packet.lastPromotedDate) || null,
            year: pendingChanges.year ?? packet.year,
            summary: form.watch('summary') ?? packet.summary ?? '',
            managerId: packet.managerId,
          }}
          managerName={managerName}
          coManagers={coManagers}
          pendingCoManagerInvitations={pendingCoManagerInvitations}
          showCoManagers
          canInviteCoManagers={canInviteCoManagers}
          isInvitingCoManagers={sendCoManagerInvitations.isPending}
          coManagerInviteInput={coManagerEmailInput}
          coManagerValidationError={coManagerValidationError}
          onCoManagerInviteInputChange={(value) => {
            setCoManagerEmailInput(value);
            setCoManagerValidationError('');
          }}
          onRemoveCoManager={(userId) => {
            setRemovedCoManagerIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
            setCoManagerValidationError('');
          }}
          onRemovePendingCoManagerInvitation={(invitationId) => {
            setRemovedCoManagerInvitationIds(prev =>
              prev.includes(invitationId) ? prev : [...prev, invitationId]
            );
            setCoManagerValidationError('');
          }}
          seniorLeaderOptions={seniorLeaderOptions}
          titleOptions={titleList}
          assessmentYearOptions={assessmentYearOptions}
          disabled={isSaving}
          readOnly={readOnly}
          isLoadingLeaders={seniorLeadersQuery.isLoading}
          errorLeaders={seniorLeadersQuery.error?.message}
          onFieldChange={handleFieldChange}
          currentUserRole={currentUserRole}
          packetId={packet.id}
          ratification={packet.ratification}
          canViewCompletedDetails={canViewCompletedDetails}
          packetStatus={packet.status}
          decision={packet.decision ?? null}
          summaryMarkdownMode={summaryMarkdownMode}
          onSummaryMarkdownModeChange={setSummaryMarkdownMode}
          isFocused={isFocused}
        />

        {isFocused && (
          <div className="flex justify-end gap-2 mt-4 col-span-1 md:col-span-2">
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                aria-label="Delete packet"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button type="button" variant="outline" onClick={handleCancelClick} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || (!hasPacketFieldChanges && !hasCoManagerInviteInput && !hasCoManagerRemovals)}
            >
              {isSaving ? (
                <> <Spinner className="mr-2 h-4 w-4" /> Saving... </>
              ) : 'Save'}
            </Button>
          </div>
        )}
      </form>

      {onDelete && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Packet</AlertDialogTitle>
              <AlertDialogDescription>Are you sure? This action cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { onDelete(); setShowDeleteConfirm(false); }} disabled={isSaving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {showCancelConfirm && (
        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard changes?</AlertDialogTitle>
              <AlertDialogDescription>You have unsaved changes. Discard them?</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Continue Editing</AlertDialogCancel>
              <AlertDialogAction onClick={handleDiscardChanges} disabled={isSaving} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </section>
  );
}
